import { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useTour } from '../state/TourContext';
import TOUR_STEPS from './tourSteps';
import '../styles/tour.css';

const VIEWPORT_MARGIN = 12;
const TOOLTIP_GAP = 12;

/**
 * Compute tooltip position clamped within the viewport.
 * Uses the measured tooltip size to ensure it never overflows.
 */
function computeTooltipPosition(placement, targetRect, tooltipW, tooltipH, pad) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let top = null;
  let left = null;
  let effectivePlacement = placement;

  if (placement === 'top' || placement === 'bottom') {
    // Horizontal: center on target, then clamp
    left = targetRect.left + targetRect.width / 2 - tooltipW / 2;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - tooltipW - VIEWPORT_MARGIN));

    if (placement === 'top') {
      top = targetRect.top - pad - TOOLTIP_GAP - tooltipH;
      // Flip to bottom if it would go offscreen
      if (top < VIEWPORT_MARGIN) {
        top = targetRect.top + targetRect.height + pad + TOOLTIP_GAP;
        effectivePlacement = 'bottom';
      }
    } else {
      top = targetRect.top + targetRect.height + pad + TOOLTIP_GAP;
      // Flip to top if it would go offscreen
      if (top + tooltipH > vh - VIEWPORT_MARGIN) {
        top = targetRect.top - pad - TOOLTIP_GAP - tooltipH;
        effectivePlacement = 'top';
      }
    }
  } else if (placement === 'right' || placement === 'left') {
    // Vertical: center on target, then clamp
    top = targetRect.top + targetRect.height / 2 - tooltipH / 2;
    top = Math.max(VIEWPORT_MARGIN, Math.min(top, vh - tooltipH - VIEWPORT_MARGIN));

    if (placement === 'right') {
      left = targetRect.left + targetRect.width + pad + TOOLTIP_GAP;
      if (left + tooltipW > vw - VIEWPORT_MARGIN) {
        left = targetRect.left - pad - TOOLTIP_GAP - tooltipW;
        effectivePlacement = 'left';
      }
    } else {
      left = targetRect.left - pad - TOOLTIP_GAP - tooltipW;
      if (left < VIEWPORT_MARGIN) {
        left = targetRect.left + targetRect.width + pad + TOOLTIP_GAP;
        effectivePlacement = 'right';
      }
    }
  }

  // Final clamp in case flipping still overflows
  top = Math.max(VIEWPORT_MARGIN, Math.min(top, vh - tooltipH - VIEWPORT_MARGIN));
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - tooltipW - VIEWPORT_MARGIN));

  // Compute arrow offset so it points at the target center, clamped within tooltip
  let arrowLeft = null;
  let arrowTop = null;
  if (effectivePlacement === 'top' || effectivePlacement === 'bottom') {
    const targetCenterX = targetRect.left + targetRect.width / 2;
    arrowLeft = Math.max(16, Math.min(targetCenterX - left, tooltipW - 16));
  } else {
    const targetCenterY = targetRect.top + targetRect.height / 2;
    arrowTop = Math.max(16, Math.min(targetCenterY - top, tooltipH - 16));
  }

  return { top, left, effectivePlacement, arrowLeft, arrowTop };
}

function TourOverlay() {
  const { active, stepIndex, nextStep, prevStep, endTour } = useTour();
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const overlayRef = useRef(null);
  const tooltipRef = useRef(null);

  const step = TOUR_STEPS[stepIndex];
  const isLastStep = stepIndex === TOUR_STEPS.length - 1;
  const isFirstStep = stepIndex === 0;

  // Measure target element position
  const measureTarget = useCallback(() => {
    if (!step?.target) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    } else {
      setTargetRect(null);
    }
  }, [step]);

  useEffect(() => {
    if (!active) return;
    measureTarget();
    window.addEventListener('resize', measureTarget);
    window.addEventListener('scroll', measureTarget, true);
    return () => {
      window.removeEventListener('resize', measureTarget);
      window.removeEventListener('scroll', measureTarget, true);
    };
  }, [active, stepIndex, measureTarget]);

  // Re-measure after a short delay for elements that animate in
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(measureTarget, 100);
    return () => clearTimeout(t);
  }, [active, stepIndex, measureTarget]);

  // Position tooltip after render using its measured size
  useLayoutEffect(() => {
    if (!active || !step) { setTooltipPos(null); return; }
    const hasTarget = step.target && targetRect;
    if (!hasTarget) { setTooltipPos(null); return; }

    const el = tooltipRef.current;
    if (!el) return;

    const tooltipW = el.offsetWidth;
    const tooltipH = el.offsetHeight;
    const pad = 8;

    const pos = computeTooltipPosition(step.placement, targetRect, tooltipW, tooltipH, pad);
    setTooltipPos(pos);
  }, [active, step, targetRect]);

  // Keyboard navigation
  useEffect(() => {
    if (!active) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') endTour();
      else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (isLastStep) endTour();
        else nextStep();
      } else if (e.key === 'ArrowLeft' && !isFirstStep) {
        prevStep();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [active, isLastStep, isFirstStep, nextStep, prevStep, endTour]);

  if (!active || !step) return null;

  // Compute spotlight cutout
  const pad = 8;
  const hasTarget = step.target && targetRect;

  const spotlightStyle = hasTarget
    ? {
        top: targetRect.top - pad,
        left: targetRect.left - pad,
        width: targetRect.width + pad * 2,
        height: targetRect.height + pad * 2,
      }
    : null;

  // Tooltip class and style
  let tooltipClass = 'tour-tooltip';
  const tooltipStyle = {};

  if (!hasTarget) {
    tooltipClass += ' tour-tooltip-center';
  } else if (tooltipPos) {
    tooltipClass += ` tour-tooltip-${tooltipPos.effectivePlacement}`;
    tooltipStyle.top = tooltipPos.top;
    tooltipStyle.left = tooltipPos.left;
    if (tooltipPos.arrowLeft != null) {
      tooltipStyle['--arrow-left'] = `${tooltipPos.arrowLeft}px`;
    }
    if (tooltipPos.arrowTop != null) {
      tooltipStyle['--arrow-top'] = `${tooltipPos.arrowTop}px`;
    }
  } else {
    // First render: position offscreen so we can measure, then layoutEffect clamps it
    tooltipClass += ` tour-tooltip-${step.placement}`;
    tooltipStyle.visibility = 'hidden';
    tooltipStyle.top = 0;
    tooltipStyle.left = 0;
  }

  return (
    <div className="tour-overlay" ref={overlayRef} onClick={endTour}>
      {/* Dark overlay with spotlight cutout */}
      <svg className="tour-overlay-svg" width="100%" height="100%">
        <defs>
          <mask id="tour-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {hasTarget && (
              <rect
                x={spotlightStyle.left}
                y={spotlightStyle.top}
                width={spotlightStyle.width}
                height={spotlightStyle.height}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(0, 0, 0, 0.65)"
          mask="url(#tour-spotlight-mask)"
        />
      </svg>

      {/* Spotlight ring glow */}
      {hasTarget && (
        <div className="tour-spotlight-ring" style={spotlightStyle} />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={tooltipClass}
        style={tooltipStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tour-tooltip-header">
          <span className="tour-tooltip-title">{step.title}</span>
          <span className="tour-tooltip-counter">
            {stepIndex + 1} / {TOUR_STEPS.length}
          </span>
        </div>
        <p className="tour-tooltip-text">{step.text}</p>
        <div className="tour-tooltip-actions">
          <button className="tour-btn-skip" onClick={endTour}>
            Skip
          </button>
          <div className="tour-tooltip-nav">
            {!isFirstStep && (
              <button className="tour-btn-back" onClick={prevStep}>
                Back
              </button>
            )}
            <button
              className="tour-btn-next"
              onClick={isLastStep ? endTour : nextStep}
            >
              {isLastStep ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TourOverlay;
